import type { CreateSlaPolicy, SlaPolicy, UpdateSlaPolicy } from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';

const slaPath = (projectId: string) => adminPath(`/projects/${projectId}/sla-policies`);

/** `GET /admin/v1/projects/{id}/sla-policies` (array). */
export function useSlaPolicies(projectId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.project(projectId ?? '').slaPolicies,
    queryFn: ({ signal }) => request<SlaPolicy[]>('GET', slaPath(projectId ?? ''), { signal }),
    enabled: projectId !== undefined,
  });
}

export function useSlaPolicy(projectId: string | undefined, policyId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.project(projectId ?? '').slaPolicy(policyId ?? ''),
    queryFn: ({ signal }) =>
      request<SlaPolicy>('GET', `${slaPath(projectId ?? '')}/${policyId}`, { signal }),
    enabled: projectId !== undefined && policyId !== undefined,
  });
}

export function useCreateSlaPolicy(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateSlaPolicy) =>
      request<SlaPolicy>('POST', slaPath(projectId), { body: input }),
    onSuccess: () =>
      invalidate(keys.project(projectId).slaPolicies, keys.projects.detail(projectId)),
  });
}

export function useUpdateSlaPolicy(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateSlaPolicy }) =>
      request<SlaPolicy>('PATCH', `${slaPath(projectId)}/${id}`, { body: patch }),
    onSuccess: (_p, { id }) =>
      invalidate(
        keys.project(projectId).slaPolicies,
        keys.project(projectId).slaPolicy(id),
        keys.projects.detail(projectId),
      ),
  });
}

export function useDeleteSlaPolicy(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<unknown>('DELETE', `${slaPath(projectId)}/${id}`),
    onSuccess: () =>
      invalidate(keys.project(projectId).slaPolicies, keys.projects.detail(projectId)),
  });
}
